import React, { useCallback, useEffect, useMemo, useRef } from "react";
import maplibregl from "maplibre-gl";
import * as turf from "@turf/turf";
import "maplibre-gl/dist/maplibre-gl.css";
import { buildFleetClusters, isValidMapCoordinate } from "../../../utils/mapClustering";
import {
  getCanonicalVehicleStatus,
  getResolvedLiveSnapshot,
  isValidGPS,
} from "../../../utils/vehicleStatus";
import {
  createFallbackRasterStyle,
  createMapLibreAdapter,
  fitMapToPoints,
  getPlainMapView,
  normalizeMapCenter,
  resolveOsmStyleUrl,
} from "./mapProviderUtils";

const DEFAULT_CENTER = { lat: 20, lng: 0 };
const DEFAULT_ZOOM = 2;
const FOLLOW_MIN_ZOOM = 16;

const popupCardStyle = `
  width: 300px;
  max-width: calc(100vw - 32px);
  border-radius: 18px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(15,23,42,0.18);
  color: #0f172a;
  font-family: Roboto, sans-serif;
`;

const popupHeaderStyle = `
  padding: 14px 16px;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%);
  color: #ffffff;
`;

const popupBodyStyle = `
  padding: 14px 16px 16px;
  display: grid;
  gap: 8px;
  font-size: 12px;
`;

const markerElementStyle = (size, selected) => `
  width:${size}px;
  height:${size}px;
  display:grid;
  place-items:center;
  transform-origin:center center;
  transition:transform 180ms linear, filter 180ms ease, width 180ms ease, height 180ms ease;
  filter:${selected ? "drop-shadow(0 0 8px rgba(37,99,235,0.85))" : "none"};
`;

const clusterElementStyle = (size) => `
  width:${size}px;
  height:${size}px;
  border-radius:999px;
  background:rgba(37,99,235,0.92);
  color:#fff;
  display:grid;
  place-items:center;
  font-weight:800;
  border:2px solid #fff;
  box-shadow:0 10px 20px rgba(15,23,42,0.22);
  cursor:pointer;
  user-select:none;
`;

const getClusterSize = (count) => {
  if (count >= 1000) return 66;
  if (count >= 250) return 58;
  if (count >= 100) return 52;
  if (count >= 25) return 46;
  return 40;
};

const getMarkerSize = (zoom = 5) => {
  if (zoom <= 4) return 14;
  if (zoom <= 6) return 18;
  if (zoom <= 8) return 22;
  if (zoom <= 10) return 24;
  if (zoom <= 14) return 28;
  if (zoom <= 16) return 32;
  return 36;
};

const toNumber = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDateTime = (value) => {
  const ts = new Date(value || 0).getTime();
  if (!ts) return "-";
  return new Date(ts).toLocaleString("en-IN");
};

const formatAddress = (device) =>
  device?.address?.full ||
  device?.address?.short ||
  device?.address ||
  device?.currentLocation?.address ||
  device?.currentAddress ||
  device?.locationAddress ||
  device?.lastKnownAddress ||
  "Address unavailable";

const resolveVehiclePoint = (device, socketUpdatesRef) => {
  const socketData =
    socketUpdatesRef?.current?.[device?._id] ||
    socketUpdatesRef?.current?.[device?.imei] ||
    {};
  const snapshot = getResolvedLiveSnapshot(device, socketData);

  const lat =
    snapshot.latitude ??
    toNumber(device?.currentLocation?.latitude ?? device?.currentLocation?.lat) ??
    toNumber(device?.liveData?.latitude ?? device?.liveData?.lat) ??
    toNumber(device?.geoLocation?.coordinates?.[1]) ??
    toNumber(device?.location?.latitude ?? device?.location?.lat) ??
    toNumber(device?.latitude ?? device?.lat);
  const lng =
    snapshot.longitude ??
    toNumber(device?.currentLocation?.longitude ?? device?.currentLocation?.lng) ??
    toNumber(device?.liveData?.longitude ?? device?.liveData?.lng) ??
    toNumber(device?.geoLocation?.coordinates?.[0]) ??
    toNumber(device?.location?.longitude ?? device?.location?.lng) ??
    toNumber(device?.longitude ?? device?.lng);

  if (!isValidGPS(Number(lat), Number(lng))) return null;

  return {
    id: String(device?._id || device?.imei || ""),
    lat: Number(lat),
    lng: Number(lng),
    heading: Number(snapshot.heading || 0),
    speed: Number(snapshot.speed || 0),
    accStatus: snapshot.accStatus,
    status: getCanonicalVehicleStatus(device, socketData),
    device,
    timestampMs: Number(snapshot.timestampMs || 0),
  };
};

const createPopupNode = (vehicle) => {
  const root = document.createElement("div");
  root.style.cssText = popupCardStyle;
  const header = document.createElement("div");
  header.style.cssText = popupHeaderStyle;
  header.innerHTML = `
    <div style="font-size:14px;font-weight:800;">${vehicle?.device?.name || vehicle?.device?.imei || "Vehicle"}</div>
    <div style="font-size:11px;opacity:0.9;margin-top:4px;">${String(vehicle?.status || "inactive").replace(/_/g, " ")}</div>
  `;
  const body = document.createElement("div");
  body.style.cssText = popupBodyStyle;
  const rows = [
    ["Speed", `${Math.round(Number(vehicle?.speed || 0))} km/h`],
    ["Ignition", vehicle?.accStatus === true || vehicle?.accStatus === "on" ? "On" : "Off"],
    ["Address", formatAddress(vehicle?.device)],
    ["Last update", formatDateTime(vehicle?.timestampMs)],
    ["Device", vehicle?.device?.imei || vehicle?.device?._id || "NA"],
  ];
  rows.forEach(([label, value]) => {
    const row = document.createElement("div");
    row.style.cssText =
      "display:grid;grid-template-columns:88px 1fr;gap:8px;align-items:start;";
    row.innerHTML = `<strong style="color:#475569;">${label}</strong><span>${value || "-"}</span>`;
    body.appendChild(row);
  });
  root.appendChild(header);
  root.appendChild(body);
  return root;
};

const setGeoJsonData = (map, sourceId, data) => {
  const source = map.getSource(sourceId);
  if (source?.setData) {
    source.setData(data);
  }
};

const hasMapLayer = (map, layerId) => {
  if (!map || typeof map.getStyle !== "function") return false;
  try {
    const style = map.getStyle();
    if (!style || !Array.isArray(style.layers)) return false;
    return style.layers.some((layer) => layer?.id === layerId);
  } catch {
    return false;
  }
};

const hasMapSource = (map, sourceId) => {
  if (!map || typeof map.getStyle !== "function") return false;
  try {
    const style = map.getStyle();
    if (!style || !style.sources) return false;
    return Boolean(style.sources[sourceId]);
  } catch {
    return false;
  }
};

export default function OSMMapProvider({
  setMap,
  setFollowMode,
  followMode,
  devicesOnMap = [],
  selectedDevices = [],
  setSelectedDevices,
  handleDeviceClick,
  onSelectLandmark,
  selectedLandmark = null,
  selectedGeofence = null,
  sharedGeofences = [],
  landmarks = [],
  draftLandmark = null,
  landmarkFocusPoint = null,
  selectedNotification = null,
  routeStart,
  routeEnd,
  forceRoute = null,
  forceRoutes = null,
  playbackData = null,
  showTraffic,
  getVehicleIcon,
  socketUpdatesRef,
  onViewportChange,
  activePopupDeviceId = null,
  onPopupDeviceChange,
  initialView = null,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const adapterRef = useRef(null);
  const markerRefs = useRef(new Map());
  const clusterRefs = useRef(new Map());
  const popupRef = useRef(null);
  const didFallbackStyleRef = useRef(false);
  const onViewportChangeRef = useRef(onViewportChange);
  const onPopupDeviceChangeRef = useRef(onPopupDeviceChange);
  const setFollowModeRef = useRef(setFollowMode);
  const geofenceCollectionRef = useRef({ type: "FeatureCollection", features: [] });
  const landmarksCollectionRef = useRef({ type: "FeatureCollection", features: [] });
  const previewLandmarkCollectionRef = useRef({ type: "FeatureCollection", features: [] });
  const routeCollectionRef = useRef({ type: "FeatureCollection", features: [] });
  const notificationFeatureRef = useRef(null);

  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const fallbackStyle = useMemo(
    () => createFallbackRasterStyle(prefersDark ? "dark" : "light"),
    [prefersDark]
  );
  const styleUrl = useMemo(
    () => resolveOsmStyleUrl(prefersDark ? "dark" : "light"),
    [prefersDark]
  );

  const viewState = useMemo(() => initialView || {
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
  }, [initialView]);

  const vehicles = useMemo(
    () =>
      (Array.isArray(devicesOnMap) ? devicesOnMap : [])
        .map((device) => resolveVehiclePoint(device, socketUpdatesRef))
        .filter(Boolean),
    [devicesOnMap, socketUpdatesRef]
  );

  const popupVehicle = useMemo(() => {
    if (!activePopupDeviceId) return null;
    return vehicles.find((vehicle) => vehicle.id === String(activePopupDeviceId)) || null;
  }, [activePopupDeviceId, vehicles]);

  const clusterPresentation = useMemo(() => {
    const map = mapRef.current;
    const bounds = map?.getBounds?.();
    return buildFleetClusters(vehicles, {
      zoom: Number(map?.getZoom?.() || viewState.zoom || DEFAULT_ZOOM),
      bounds: bounds
        ? {
            north: bounds.getNorth(),
            east: bounds.getEast(),
            south: bounds.getSouth(),
            west: bounds.getWest(),
          }
        : null,
      selectedIds: new Set((Array.isArray(selectedDevices) ? selectedDevices : []).map(String)),
      gridSizePx: 72,
    });
  }, [selectedDevices, vehicles, viewState.zoom]);

  const geofenceCollection = useMemo(() => {
    const features = [];
    (Array.isArray(sharedGeofences) ? sharedGeofences : []).forEach((geofence, index) => {
      const id = String(geofence?.geofenceId || geofence?._id || geofence?.id || `geofence-${index}`);
      const selected = String(selectedGeofence?.id || "") === id;
      if (geofence?.type === "circle" && geofence?.center) {
        const lat = Number(geofence.center.lat);
        const lng = Number(geofence.center.lng);
        const radiusMeters = Math.max(0, Number(geofence.radius || 0));
        if (!isValidGPS(lat, lng) || !radiusMeters) return;
        const feature = turf.circle([lng, lat], radiusMeters / 1000, {
          steps: 64,
          units: "kilometers",
          properties: { id, selected, label: geofence?.name || "Geofence" },
        });
        features.push(feature);
      } else if (geofence?.type === "polygon" && geofence?.polygon?.coordinates?.[0]) {
        features.push({
          type: "Feature",
          properties: { id, selected, label: geofence?.name || "Geofence" },
          geometry: {
            type: "Polygon",
            coordinates: geofence.polygon.coordinates,
          },
        });
      }
    });
    return { type: "FeatureCollection", features };
  }, [selectedGeofence, sharedGeofences]);

  const landmarksCollection = useMemo(() => {
    const features = [];
    (Array.isArray(landmarks) ? landmarks : []).forEach((landmark, index) => {
      const lat = Number(landmark?.latitude ?? landmark?.lat);
      const lng = Number(landmark?.longitude ?? landmark?.lng);
      if (!isValidGPS(lat, lng)) return;
      features.push({
        type: "Feature",
        properties: {
          id: String(landmark?._id || landmark?.id || `landmark-${index}`),
          selected:
            String(selectedLandmark?.id || "") ===
            String(landmark?._id || landmark?.id || `landmark-${index}`),
          label: landmark?.name || "Landmark",
          radius: Math.max(0, Number(landmark?.radius ?? landmark?.radiusMeters ?? 30)),
        },
        geometry: {
          type: "Point",
          coordinates: [lng, lat],
        },
      });
    });
    return { type: "FeatureCollection", features };
  }, [landmarks, selectedLandmark]);

  const previewLandmarkCollection = useMemo(() => {
    const lat = Number(draftLandmark?.latitude ?? draftLandmark?.lat);
    const lng = Number(draftLandmark?.longitude ?? draftLandmark?.lng);
    if (!isValidGPS(lat, lng)) return { type: "FeatureCollection", features: [] };
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {
            label: "Landmark preview",
            radius: Math.max(0, Number(draftLandmark?.radius ?? draftLandmark?.radiusMeters ?? 30)),
          },
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
        },
      ],
    };
  }, [draftLandmark]);

  const routeCollection = useMemo(() => {
    const features = [];
    const overlayRoutes = [
      ...(Array.isArray(forceRoutes) ? forceRoutes : []),
      ...(forceRoute ? [forceRoute] : []),
    ];
    overlayRoutes.forEach((route, index) => {
      const start = route?.start || route?.source;
      const end = route?.end || route?.destination;
      const sLat = Number(start?.lat);
      const sLng = Number(start?.lng);
      const eLat = Number(end?.lat);
      const eLng = Number(end?.lng);
      if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) return;
      features.push({
        type: "Feature",
        properties: { kind: "route", id: String(route?._id || route?.id || index) },
        geometry: {
          type: "LineString",
          coordinates: [
            [sLng, sLat],
            [eLng, eLat],
          ],
        },
      });
      features.push({
        type: "Feature",
        properties: { kind: "route-point", role: "start" },
        geometry: { type: "Point", coordinates: [sLng, sLat] },
      });
      features.push({
        type: "Feature",
        properties: { kind: "route-point", role: "end" },
        geometry: { type: "Point", coordinates: [eLng, eLat] },
      });
    });

    if (routeStart && routeEnd && routeStart.length >= 2 && routeEnd.length >= 2) {
      features.push({
        type: "Feature",
        properties: { kind: "route", id: "manual-route" },
        geometry: {
          type: "LineString",
          coordinates: [
            [Number(routeStart[1]), Number(routeStart[0])],
            [Number(routeEnd[1]), Number(routeEnd[0])],
          ],
        },
      });
    }

    if (Array.isArray(playbackData?.route) && playbackData.route.length > 1) {
      features.push({
        type: "Feature",
        properties: { kind: "playback", id: String(playbackData?.id || "playback") },
        geometry: {
          type: "LineString",
          coordinates: playbackData.route
            .filter((point) => isValidMapCoordinate(point?.lat, point?.lng))
            .map((point) => [Number(point.lng), Number(point.lat)]),
        },
      });
    }

    return { type: "FeatureCollection", features };
  }, [forceRoute, forceRoutes, playbackData, routeEnd, routeStart]);

  const notificationFeature = useMemo(() => {
    const lat = Number(
      selectedNotification?.location?.latitude ??
      selectedNotification?.location?.lat ??
      selectedNotification?.latitude ??
      selectedNotification?.lat
    );
    const lng = Number(
      selectedNotification?.location?.longitude ??
      selectedNotification?.location?.lng ??
      selectedNotification?.longitude ??
      selectedNotification?.lng
    );
    if (!isValidGPS(lat, lng)) return null;
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {
            label:
              selectedNotification?.deviceName ||
              selectedNotification?.imei ||
              "Notification",
          },
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
        },
      ],
    };
  }, [selectedNotification]);

  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);

  useEffect(() => {
    onPopupDeviceChangeRef.current = onPopupDeviceChange;
  }, [onPopupDeviceChange]);

  useEffect(() => {
    setFollowModeRef.current = setFollowMode;
  }, [setFollowMode]);

  useEffect(() => {
    geofenceCollectionRef.current = geofenceCollection;
  }, [geofenceCollection]);

  useEffect(() => {
    landmarksCollectionRef.current = landmarksCollection;
  }, [landmarksCollection]);

  useEffect(() => {
    previewLandmarkCollectionRef.current = previewLandmarkCollection;
  }, [previewLandmarkCollection]);

  useEffect(() => {
    routeCollectionRef.current = routeCollection;
  }, [routeCollection]);

  useEffect(() => {
    notificationFeatureRef.current = notificationFeature;
  }, [notificationFeature]);

  const publishViewport = useCallback(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;
    const view = getPlainMapView(adapter, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    const bounds = adapter.getBounds?.();
    const northEast = bounds?.getNorthEast?.();
    const southWest = bounds?.getSouthWest?.();
    if (!northEast || !southWest) return;
    onViewportChangeRef.current?.({
      zoom: view.zoom,
      center: view.center,
      bounds: {
        north: northEast.lat(),
        east: northEast.lng(),
        south: southWest.lat(),
        west: southWest.lng(),
      },
    });
  }, [onViewportChange]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleUrl || fallbackStyle,
      center: [viewState.center.lng, viewState.center.lat],
      zoom: Number(viewState.zoom || DEFAULT_ZOOM),
      attributionControl: false,
      dragRotate: true,
      touchPitch: true,
      cooperativeGestures: false,
    });

    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "bottom-right");
    map.addControl(new maplibregl.FullscreenControl(), "bottom-right");
    map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
      }),
      "bottom-right"
    );
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");
    map.addControl(new maplibregl.AttributionControl({ compact: true }));

    mapRef.current = map;
    adapterRef.current = createMapLibreAdapter(map);
    setMap?.(adapterRef.current);
    const markerStore = markerRefs.current;
    const clusterStore = clusterRefs.current;

    const ensureOverlayLayers = () => {
      const nextGeofences = geofenceCollectionRef.current;
      const nextLandmarks = landmarksCollectionRef.current;
      const nextPreviewLandmark = previewLandmarkCollectionRef.current;
      const nextRoutes = routeCollectionRef.current;

      if (!hasMapSource(map, "osm-geofences")) {
        map.addSource("osm-geofences", { type: "geojson", data: nextGeofences });
      }
      if (!hasMapLayer(map, "osm-geofence-fill")) {
        map.addLayer({
          id: "osm-geofence-fill",
          type: "fill",
          source: "osm-geofences",
          paint: {
            "fill-color": ["case", ["boolean", ["get", "selected"], false], "#3b82f6", "#10b981"],
            "fill-opacity": ["case", ["boolean", ["get", "selected"], false], 0.22, 0.12],
          },
        });
      }
      if (!hasMapLayer(map, "osm-geofence-line")) {
        map.addLayer({
          id: "osm-geofence-line",
          type: "line",
          source: "osm-geofences",
          paint: {
            "line-color": ["case", ["boolean", ["get", "selected"], false], "#2563eb", "#059669"],
            "line-width": ["case", ["boolean", ["get", "selected"], false], 3, 2],
          },
        });
      }

      if (!hasMapSource(map, "osm-landmarks")) {
        map.addSource("osm-landmarks", { type: "geojson", data: nextLandmarks });
      }
      if (!hasMapLayer(map, "osm-landmarks-circle")) {
        map.addLayer({
          id: "osm-landmarks-circle",
          type: "circle",
          source: "osm-landmarks",
          paint: {
            "circle-radius": ["case", ["boolean", ["get", "selected"], false], 8, 6],
            "circle-color": ["case", ["boolean", ["get", "selected"], false], "#ea580c", "#fb923c"],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
      }

      if (!hasMapSource(map, "osm-preview-landmark")) {
        map.addSource("osm-preview-landmark", { type: "geojson", data: nextPreviewLandmark });
      }
      if (!hasMapLayer(map, "osm-preview-landmark-circle")) {
        map.addLayer({
          id: "osm-preview-landmark-circle",
          type: "circle",
          source: "osm-preview-landmark",
          paint: {
            "circle-radius": 8,
            "circle-color": "#111827",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
      }

      if (!hasMapSource(map, "osm-routes")) {
        map.addSource("osm-routes", { type: "geojson", data: nextRoutes });
      }
      if (!hasMapLayer(map, "osm-routes-line")) {
        map.addLayer({
          id: "osm-routes-line",
          type: "line",
          source: "osm-routes",
          filter: ["==", ["get", "kind"], "route"],
          paint: {
            "line-color": "#2563eb",
            "line-width": 4,
            "line-opacity": 0.9,
          },
        });
      }
      if (!hasMapLayer(map, "osm-playback-line")) {
        map.addLayer({
          id: "osm-playback-line",
          type: "line",
          source: "osm-routes",
          filter: ["==", ["get", "kind"], "playback"],
          paint: {
            "line-color": "#7c3aed",
            "line-width": 4,
            "line-opacity": 0.92,
          },
        });
      }
      if (!hasMapLayer(map, "osm-route-points")) {
        map.addLayer({
          id: "osm-route-points",
          type: "circle",
          source: "osm-routes",
          filter: ["==", ["get", "kind"], "route-point"],
          paint: {
            "circle-radius": 7,
            "circle-color": [
              "case",
              ["==", ["get", "role"], "start"],
              "#16a34a",
              "#ef4444",
            ],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
      }

      publishViewport();
    };

    map.on("error", (event) => {
      if (!styleUrl || didFallbackStyleRef.current) return;
      const message = String(event?.error?.message || event?.error || "");
      if (!message) return;
      didFallbackStyleRef.current = true;
      console.warn("[OSM MAP] Falling back to bundled raster style:", message);
      map.setStyle(fallbackStyle);
    });

    map.on("style.load", ensureOverlayLayers);
    map.on("load", () => {
      map.on("moveend", publishViewport);
      map.on("zoomend", publishViewport);
      map.on("click", () => {
        popupRef.current?.remove?.();
        popupRef.current = null;
        onPopupDeviceChangeRef.current?.(null);
        setFollowModeRef.current?.(false);
      });
    });

    return () => {
      popupRef.current?.remove?.();
      popupRef.current = null;
      markerStore.forEach((entry) => entry.marker.remove());
      markerStore.clear();
      clusterStore.forEach((marker) => marker.remove());
      clusterStore.clear();
      map.remove();
      mapRef.current = null;
      adapterRef.current = null;
      setMap?.(null);
    };
  }, [
    setMap,
    fallbackStyle,
    styleUrl,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded?.()) return;
    setGeoJsonData(map, "osm-geofences", geofenceCollection);
    setGeoJsonData(map, "osm-landmarks", landmarksCollection);
    setGeoJsonData(map, "osm-preview-landmark", previewLandmarkCollection);
    setGeoJsonData(map, "osm-routes", routeCollection);
  }, [geofenceCollection, landmarksCollection, previewLandmarkCollection, routeCollection]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded?.()) return;

    if (notificationFeature) {
      if (!hasMapSource(map, "osm-notification")) {
        map.addSource("osm-notification", { type: "geojson", data: notificationFeature });
        map.addLayer({
          id: "osm-notification-circle",
          type: "circle",
          source: "osm-notification",
          paint: {
            "circle-radius": 8,
            "circle-color": "#ef4444",
            "circle-stroke-width": 3,
            "circle-stroke-color": "#ffffff",
          },
        });
      } else {
        setGeoJsonData(map, "osm-notification", notificationFeature);
      }
    } else if (hasMapLayer(map, "osm-notification-circle")) {
      map.removeLayer("osm-notification-circle");
      if (hasMapSource(map, "osm-notification")) {
        map.removeSource("osm-notification");
      }
    }
  }, [notificationFeature]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const zoom = Number(map.getZoom?.() || viewState.zoom || DEFAULT_ZOOM);
    const selectedIds = new Set((Array.isArray(selectedDevices) ? selectedDevices : []).map(String));
    const activeMarkerIds = new Set();

    clusterPresentation.singles.forEach((vehicle) => {
      activeMarkerIds.add(vehicle.id);
      let entry = markerRefs.current.get(vehicle.id);
      if (!entry) {
        const element = document.createElement("div");
        const image = document.createElement("img");
        image.alt = vehicle?.device?.name || "Vehicle";
        image.draggable = false;
        image.style.cssText = "width:100%;height:100%;object-fit:contain;";
        element.appendChild(image);
        const marker = new maplibregl.Marker({
          element,
          anchor: "center",
          rotationAlignment: "map",
          pitchAlignment: "map",
        })
          .setLngLat([vehicle.lng, vehicle.lat])
          .addTo(map);
        element.addEventListener("click", (event) => {
          event.stopPropagation();
          setSelectedDevices?.([vehicle.device._id]);
          handleDeviceClick?.(vehicle.device);
          onPopupDeviceChange?.(vehicle.id);
          setFollowMode?.(true);
        });
        entry = { marker, element, image };
        markerRefs.current.set(vehicle.id, entry);
      }

      const iconSrc =
        (typeof getVehicleIcon === "function" &&
          getVehicleIcon(vehicle?.device?.vehicleType || vehicle?.device?.deviceIcon || "car")) ||
        "";
      const size = getMarkerSize(zoom);
      entry.element.style.cssText = markerElementStyle(size, selectedIds.has(vehicle.id));
      entry.image.src = iconSrc;
      entry.image.style.transform = `rotate(${Math.round(Number(vehicle.heading || 0))}deg)`;
      entry.marker.setLngLat([vehicle.lng, vehicle.lat]);
    });

    markerRefs.current.forEach((entry, markerId) => {
      if (activeMarkerIds.has(markerId)) return;
      entry.marker.remove();
      markerRefs.current.delete(markerId);
    });

    const activeClusterIds = new Set();
    clusterPresentation.clusters.forEach((cluster) => {
      activeClusterIds.add(cluster.id);
      let marker = clusterRefs.current.get(cluster.id);
      if (!marker) {
        const element = document.createElement("div");
        element.addEventListener("click", (event) => {
          event.stopPropagation();
          fitMapToPoints(adapterRef.current, cluster.members, 80);
        });
        marker = new maplibregl.Marker({ element, anchor: "center" })
          .setLngLat([Number(cluster.lng), Number(cluster.lat)])
          .addTo(map);
        clusterRefs.current.set(cluster.id, marker);
      }
      const size = getClusterSize(Number(cluster.count || 0));
      marker.getElement().style.cssText = clusterElementStyle(size);
      marker.getElement().textContent = String(cluster.count || 0);
      marker.setLngLat([Number(cluster.lng), Number(cluster.lat)]);
    });

    clusterRefs.current.forEach((marker, clusterId) => {
      if (activeClusterIds.has(clusterId)) return;
      marker.remove();
      clusterRefs.current.delete(clusterId);
    });
  }, [
    clusterPresentation.clusters,
    clusterPresentation.singles,
    getVehicleIcon,
    handleDeviceClick,
    onPopupDeviceChange,
    selectedDevices,
    setFollowMode,
    setSelectedDevices,
    viewState.zoom,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !popupVehicle) {
      popupRef.current?.remove?.();
      popupRef.current = null;
      return;
    }

    popupRef.current?.remove?.();
    const popup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: false,
      offset: 18,
      maxWidth: "340px",
    })
      .setLngLat([popupVehicle.lng, popupVehicle.lat])
      .setDOMContent(createPopupNode(popupVehicle))
      .addTo(map);
    popup.on("close", () => onPopupDeviceChange?.(null));
    popupRef.current = popup;
  }, [onPopupDeviceChange, popupVehicle]);

  useEffect(() => {
    if (!followMode || !popupVehicle) return;
    const adapter = adapterRef.current;
    if (!adapter) return;
    adapter.panTo({ lat: popupVehicle.lat, lng: popupVehicle.lng });
    const currentZoom = Number(adapter.getZoom?.() || 0);
    if (currentZoom < FOLLOW_MIN_ZOOM) {
      adapter.setZoom(FOLLOW_MIN_ZOOM);
    }
  }, [followMode, popupVehicle]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedLandmark) return;
    const lat = Number(selectedLandmark?.latitude ?? selectedLandmark?.lat);
    const lng = Number(selectedLandmark?.longitude ?? selectedLandmark?.lng);
    if (!isValidGPS(lat, lng)) return;
    adapterRef.current?.panTo({ lat, lng });
  }, [selectedLandmark]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !landmarkFocusPoint || draftLandmark) return;
    const lat = Number(landmarkFocusPoint?.latitude ?? landmarkFocusPoint?.lat);
    const lng = Number(landmarkFocusPoint?.longitude ?? landmarkFocusPoint?.lng);
    if (!isValidGPS(lat, lng)) return;
    adapterRef.current?.panTo({ lat, lng });
  }, [draftLandmark, landmarkFocusPoint]);

  useEffect(() => {
    if (!selectedGeofence?.id || !adapterRef.current) return;
    const target = (Array.isArray(sharedGeofences) ? sharedGeofences : []).find(
      (item) =>
        String(item?.geofenceId || item?._id || item?.id || "") === String(selectedGeofence.id)
    );
    if (!target) return;
    if (target?.type === "circle" && target?.center) {
      adapterRef.current.panTo(normalizeMapCenter(target.center, DEFAULT_CENTER));
      return;
    }
    if (target?.type === "polygon" && target?.polygon?.coordinates?.[0]) {
      fitMapToPoints(
        adapterRef.current,
        target.polygon.coordinates[0].map((coordinate) => ({
          lat: Number(coordinate[1]),
          lng: Number(coordinate[0]),
        })),
        80
      );
    }
  }, [selectedGeofence, sharedGeofences]);

  useEffect(() => {
    if (!selectedNotification || !adapterRef.current) return;
    const lat = Number(
      selectedNotification?.location?.latitude ??
      selectedNotification?.location?.lat ??
      selectedNotification?.latitude ??
      selectedNotification?.lat
    );
    const lng = Number(
      selectedNotification?.location?.longitude ??
      selectedNotification?.location?.lng ??
      selectedNotification?.longitude ??
      selectedNotification?.lng
    );
    if (!isValidGPS(lat, lng)) return;
    adapterRef.current.panTo({ lat, lng });
  }, [selectedNotification]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onLandmarkClick = (event) => {
      const feature = event?.features?.[0];
      const landmarkId = String(feature?.properties?.id || "");
      const target = (Array.isArray(landmarks) ? landmarks : []).find(
        (landmark, index) =>
          String(landmark?._id || landmark?.id || `landmark-${index}`) === landmarkId
      );
      if (target) {
        onSelectLandmark?.(target);
      }
    };

    map.on("click", "osm-landmarks-circle", onLandmarkClick);
    return () => {
      if (hasMapLayer(map, "osm-landmarks-circle")) {
        map.off("click", "osm-landmarks-circle", onLandmarkClick);
      }
    };
  }, [landmarks, onSelectLandmark]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !showTraffic) return;
    // MapLibre is rendered with vector streets here; keep the flag accepted without altering
    // the existing shared business logic.
  }, [showTraffic]);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
}
